import { useState, useCallback } from 'react'
import { useAuth } from './useAuth'

// 一覧画面の検索条件（最後に適用したフィルタ・並び順・ページ）を保持するフック。
// 顧客要望によりブラウザ／タブを閉じて再起動しても保持するため localStorage を使う。
// ただし Cognito トークンは sessionStorage 管理で再起動後は再ログインになるため、共用 PC では
// localStorage が複数ユーザに跨って残る。検索条件には氏名・識別ID 等の個人情報が含まれ得るので、
// 他ユーザに見えないよう cognitoSub でスコープする（下書き機能と同じ方針）。
function useSearchPersistence<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const { session } = useAuth()
  const sub = session?.cognitoSub ?? 'anon'
  const storageKey = `search_${sub}_${key}`

  const [value, setValue] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      return saved ? JSON.parse(saved) as T : defaultValue
    } catch {
      return defaultValue
    }
  })

  const setPersistedValue = useCallback(
    (newValue: T) => {
      setValue(newValue)
      try {
        localStorage.setItem(storageKey, JSON.stringify(newValue))
      } catch {
        // ignore storage errors (quota, private mode, etc.)
      }
    },
    [storageKey],
  )

  return [value, setPersistedValue]
}

export default useSearchPersistence
